/** discovered (watch only) → listed (tradable) → halted / close_only → delisted (settled); archived = dropped from Pulse. */
export const MEME_TOKEN_STATES = ['discovered', 'listed', 'halted', 'close_only', 'delisted', 'archived'];
/** Pulse columns: new launches, tokens close to graduating from their launchpad, and tradable tokens. */
export const MEME_PULSE_COLUMNS = ['new', 'graduating', 'listed'];
export const EMPTY_MEME_METRICS = {
    priceUsd: null,
    marketCapUsd: null,
    liquidityUsd: null,
    volume24hUsd: null,
    holders: null,
    top10Pct: null,
    devPct: null,
    snipersPct: null,
    insidersPct: null,
    bundlersPct: null,
    bondingPct: null,
    mintAuthorityRevoked: null,
    freezeAuthorityRevoked: null,
    lpBurnedOrLocked: null,
    rugcheck: null,
    dexPaid: null,
    priceChange24hPct: null,
    txns24h: null,
};
export const DEFAULT_MEME_RULES = {
    listing: {
        autoApprove: false,
        requireGraduated: true,
        minLiquidityUsd: 250_000,
        minVolume24hUsd: 500_000,
        minAgeHours: 24,
        requireAuthoritiesRevoked: true,
        requireLpBurnedOrLocked: true,
        maxTop10Pct: 30,
        maxDevPct: 10,
        minHealthySources: 2,
        maxListed: 50,
    },
    pricing: {
        maxSourceDeviationPct: 3,
        staleMs: 15_000,
        emaAlpha: 0.5,
        spread: { baseBps: 40, volatilityK: 0.5, exposureK: 2000, riskK: 10, minBps: 30, maxBps: 500 },
    },
    risk: {
        tiers: [
            { maxRisk: 3, leverage: 5, clientCapUsd: 5000 },
            { maxRisk: 6, leverage: 3, clientCapUsd: 3000 },
            { maxRisk: 8, leverage: 2, clientCapUsd: 1500 },
        ],
        houseCapUsd: 500_000,
        houseCapLiquidityPct: 2,
        exposureAlertPct: 80,
        breaker: { move60sPct: 40, move15mPct: 70, haltMinutes: 5 },
        rug: { liquidityDropPct: 50, liquidityWindowMinutes: 5 },
        defaultMaxDeviationPct: 1,
        largeLossUsd: 1000,
        feeRate: 0.001,
        swapLongRate: -0.1,
        swapShortRate: -0.1,
    },
};
const NUMS = [
    ['listing.minLiquidityUsd', 0, 1e12],
    ['listing.minVolume24hUsd', 0, 1e12],
    ['listing.minAgeHours', 0, 24 * 365],
    ['listing.maxTop10Pct', 0, 100],
    ['listing.maxDevPct', 0, 100],
    ['listing.minHealthySources', 2, 10, true],
    ['listing.maxListed', 0, 1000, true],
    ['pricing.maxSourceDeviationPct', 0.1, 50],
    ['pricing.staleMs', 1000, 600_000, true],
    ['pricing.emaAlpha', 0.05, 1],
    ['pricing.spread.baseBps', 0, 2000],
    ['pricing.spread.volatilityK', 0, 100],
    ['pricing.spread.exposureK', 0, 100_000],
    ['pricing.spread.riskK', 0, 1000],
    ['pricing.spread.minBps', 0, 2000],
    ['pricing.spread.maxBps', 1, 5000],
    ['risk.houseCapUsd', 0, 1e12],
    ['risk.houseCapLiquidityPct', 0, 100],
    ['risk.exposureAlertPct', 1, 100],
    ['risk.breaker.move60sPct', 1, 1000],
    ['risk.breaker.move15mPct', 1, 1000],
    ['risk.breaker.haltMinutes', 1, 1440, true],
    ['risk.rug.liquidityDropPct', 1, 100],
    ['risk.rug.liquidityWindowMinutes', 1, 1440, true],
    ['risk.defaultMaxDeviationPct', 0.05, 50],
    ['risk.largeLossUsd', 0, 1e9],
    ['risk.feeRate', 0, 0.05],
    ['risk.swapLongRate', -5, 5],
    ['risk.swapShortRate', -5, 5],
];
const BOOLS = ['listing.autoApprove', 'listing.requireGraduated', 'listing.requireAuthoritiesRevoked', 'listing.requireLpBurnedOrLocked'];
const getPath = (o, p) => p.split('.').reduce((x, k) => (x == null ? undefined : x[k]), o);
function setPath(o, p, v) {
    const ks = p.split('.');
    let x = o;
    for (const k of ks.slice(0, -1))
        x = x[k];
    x[ks.at(-1)] = v;
}
/** Merge a partial update over `base` and check every value. Paths not sent keep their value. */
export function parseMemeRules(v, base = DEFAULT_MEME_RULES) {
    if (!v || typeof v !== 'object')
        return { ok: false, error: 'Send the rules as an object' };
    const out = JSON.parse(JSON.stringify(base));
    for (const [p, min, max, int] of NUMS) {
        const x = getPath(v, p);
        if (x === undefined)
            continue;
        if (typeof x !== 'number' || !Number.isFinite(x) || x < min || x > max || (int && !Number.isInteger(x))) {
            return { ok: false, error: `${p} must be ${int ? 'a whole number' : 'a number'} from ${min} to ${max}` };
        }
        setPath(out, p, x);
    }
    for (const p of BOOLS) {
        const x = getPath(v, p);
        if (x === undefined)
            continue;
        if (typeof x !== 'boolean')
            return { ok: false, error: `${p} must be true or false` };
        setPath(out, p, x);
    }
    const tiers = getPath(v, 'risk.tiers');
    if (tiers !== undefined) {
        if (!Array.isArray(tiers) || !tiers.length || tiers.length > 10)
            return { ok: false, error: 'risk.tiers: send 1 to 10 tiers' };
        const t = [];
        for (const x of tiers) {
            const ok = x && Number.isInteger(x.maxRisk) && x.maxRisk >= 1 && x.maxRisk <= 10 && Number.isInteger(x.leverage) && x.leverage >= 1 && x.leverage <= 20 &&
                typeof x.clientCapUsd === 'number' && x.clientCapUsd > 0 && x.clientCapUsd <= 1e9;
            if (!ok)
                return { ok: false, error: 'Each tier needs maxRisk 1–10, leverage 1–20 and clientCapUsd above 0' };
            t.push({ maxRisk: x.maxRisk, leverage: x.leverage, clientCapUsd: x.clientCapUsd });
        }
        t.sort((a, b) => a.maxRisk - b.maxRisk);
        if (new Set(t.map((x) => x.maxRisk)).size !== t.length)
            return { ok: false, error: 'Two tiers have the same maxRisk' };
        out.risk.tiers = t;
    }
    if (out.pricing.spread.minBps > out.pricing.spread.maxBps)
        return { ok: false, error: 'pricing.spread.minBps is above maxBps' };
    if (out.risk.breaker.move60sPct > out.risk.breaker.move15mPct)
        return { ok: false, error: 'risk.breaker.move60sPct is above move15mPct' };
    return { ok: true, value: out };
}
/** The tier a risk score falls in; null = too risky to list. */
export function memeTier(riskScore, rules) {
    if (riskScore == null)
        return null;
    return rules.risk.tiers.find((t) => riskScore <= t.maxRisk) ?? null;
}
/** Listing gates the token fails (empty = listable). Unknown facts fail their gate: we never list blind. */
export function memeGateFailures(g, rules, now = new Date()) {
    const L = rules.listing;
    const m = g.metrics;
    const out = [];
    const num = (v) => (v == null ? null : Number(v));
    if (L.requireGraduated && !g.graduated)
        out.push('Not graduated to a DEX pool yet');
    const liq = num(m.liquidityUsd);
    if (liq == null || liq < L.minLiquidityUsd)
        out.push(`Pool liquidity under ${L.minLiquidityUsd.toLocaleString('en-US')} USDT`);
    const vol = num(m.volume24hUsd);
    if (vol == null || vol < L.minVolume24hUsd)
        out.push(`24h volume under ${L.minVolume24hUsd.toLocaleString('en-US')} USDT`);
    if (!g.createdAt || now.getTime() - g.createdAt.getTime() < L.minAgeHours * 3600_000)
        out.push(`Younger than ${L.minAgeHours} h`);
    if (L.requireAuthoritiesRevoked && !(m.mintAuthorityRevoked === true && m.freezeAuthorityRevoked === true))
        out.push('Mint or freeze authority not revoked');
    if (L.requireLpBurnedOrLocked && m.lpBurnedOrLocked !== true)
        out.push('Liquidity not burned or locked');
    if (m.rugcheck === 'danger' || m.rugcheck == null)
        out.push(m.rugcheck == null ? 'No RugCheck report' : 'RugCheck: danger');
    if (m.top10Pct == null || m.top10Pct >= L.maxTop10Pct)
        out.push(`Top 10 holders own ${L.maxTop10Pct}% or more`);
    if (m.devPct == null || m.devPct >= L.maxDevPct)
        out.push(`Developer holds ${L.maxDevPct}% or more`);
    if (g.healthySources < L.minHealthySources)
        out.push(`Fewer than ${L.minHealthySources} healthy price sources`);
    if (!memeTier(g.riskScore, rules))
        out.push('Risk score too high (or unknown) for any leverage tier');
    if (g.listedCount >= L.maxListed)
        out.push(`Already ${L.maxListed} tokens listed`);
    return out;
}
/** Risk score 1–10 from the token's facts (10 = riskiest). Missing facts count as risky. */
export function memeRiskScore(m) {
    let s = 1;
    const add = (cond, pts) => {
        if (cond)
            s += pts;
    };
    add(m.rugcheck === 'danger', 5);
    add(m.rugcheck === 'warn' || m.rugcheck == null, 1);
    add(m.top10Pct == null || m.top10Pct >= 25, 1);
    add(m.top10Pct != null && m.top10Pct >= 40, 1);
    add(m.devPct == null || m.devPct >= 5, 1);
    add((m.snipersPct ?? 0) >= 10 || (m.insidersPct ?? 0) >= 10 || (m.bundlersPct ?? 0) >= 10, 1);
    const liq = m.liquidityUsd == null ? 0 : Number(m.liquidityUsd);
    add(liq < 1_000_000, 1);
    add(liq < 250_000, 1);
    add(m.mintAuthorityRevoked !== true || m.freezeAuthorityRevoked !== true, 2);
    add(m.lpBurnedOrLocked !== true, 1);
    return Math.min(10, s);
}
/** The meme coin appropriateness quiz. Wording to be confirmed by counsel (06-regulatory). */
export const MEME_ASSESSMENT = [
    { id: 'cfd', text: 'When you trade a meme coin here, what do you own?', options: ['The coin itself, in a wallet', 'A contract on the coin’s price (CFD); no coins change hands', 'A share in the coin’s project'] },
    { id: 'loss', text: 'You open a 100 USDT position at 3x leverage. The coin falls 34%. Roughly what happens?', options: ['You lose about 34 USDT', 'You lose about 100 USDT or more and the position may be closed', 'Nothing until you close it'] },
    { id: 'volatility', text: 'Which is true of meme coins?', options: ['Their prices are stable because many people hold them', 'Their prices can move 50% or more in minutes, up or down', 'They follow the price of Bitcoin closely'] },
    { id: 'halt', text: 'The coin’s price jumps 60% in a minute. What may the platform do?', options: ['Nothing, trading always continues', 'Pause trading on it for a few minutes, or allow closing only', 'Cancel your profitable trades'] },
    { id: 'delist', text: 'A coin is delisted after a rug pull. How are open positions closed?', options: ['At the last price before the crash, chosen by the platform', 'At the average price of the hour before the event, for longs and shorts alike', 'They stay open until the coin trades again'] },
    { id: 'money', text: 'How much of your savings should you put into meme coin trading?', options: ['Only money you can afford to lose entirely', 'Most of it, the returns are high', 'As much as the leverage allows'] },
];
const MEME_ANSWERS = { cfd: 1, loss: 1, volatility: 1, halt: 1, delist: 1, money: 0 };
export const MEME_PASS_MARK = 5;
/** After a failed attempt, the client waits this long before trying again. */
export const MEME_RETRY_HOURS = 24;
/** `answers`: question id → chosen option index. `acknowledged`: the client accepted the risk warning. */
export function scoreMemeAssessment(answers, acknowledged) {
    if (acknowledged !== true)
        return { ok: false, error: 'Confirm that you have read and understood the risk warning' };
    if (!answers || typeof answers !== 'object')
        return { ok: false, error: 'Answer every question' };
    const a = answers;
    const wrong = [];
    for (const q of MEME_ASSESSMENT) {
        const x = a[q.id];
        if (!Number.isInteger(x) || x < 0 || x >= q.options.length)
            return { ok: false, error: 'Answer every question' };
        if (x !== MEME_ANSWERS[q.id])
            wrong.push(q.id);
    }
    const score = MEME_ASSESSMENT.length - wrong.length;
    return { ok: true, value: { score, total: MEME_ASSESSMENT.length, passed: score >= MEME_PASS_MARK, wrong } };
}
export const MEME_SETTLEMENT_TEXT = 'If a coin is delisted after a risk event, every open position is closed at the average price of the hour before the event (time-weighted), for longs and shorts alike.';
/** Redis channels for the meme feed (see ws-protocol for the client side). */
export const MemeCh = {
    pulse: (column) => `meme:pulse:${column}`,
    token: (mint) => `meme:token:${mint}`,
    changed: 'meme:changed',
};
/** Redis hash with a listed token's price health, written by the feeder. */
export const memePriceKey = (symbol) => `meme:px:${symbol}`;
/** Solana mint addresses: base58, 32–44 characters. */
export const MINT_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
//# sourceMappingURL=meme.js.map